import { Box, Space, SpaceVertical, Spinner } from '@looker/components';
import { BIButton, BINotification, globalTheme } from '@pulsepointinc/bi-components';
import { DsRadio, DsRadioGroup, DsTypography } from '@pulsepointinc/design-system-react';
import { type FC, useId } from 'react';
import { useBulkUpload } from '../hooks/useBulkUpload';
import { StyledTextArea } from '../style';
import { type BulkUploadProps, MAX_BULK_ITEMS } from '../utils';
import { BulkUploadFeedback } from './BulkUploadFeedback';
import { FileDropzone } from './FileDropzone';

export const BulkUploadComponent: FC<BulkUploadProps> = ({
  title,
  onUpload,
  onCancel,
  initialExpressionType,
  disableExpression = false,
  filterName,
}) => {
  const radioGroupId = useId();
  const {
    rawInput,
    setRawInput,
    validItems,
    invalidLines,
    itemCount,
    isOverLimit,
    expressionType,
    isLoading,
    isProcessingFile,
    submissionError,
    handleSubmit,
    setExpressionType,
    handleFileDrop,
  } = useBulkUpload({
    initialType: initialExpressionType,
    onUpload,
    filterName,
  });

  return (
    <SpaceVertical
      borderRadius={'6px'}
      backgroundColor={globalTheme.colors.backgroundLight}
      gap="u4"
      padding="u6"
      height="100%"
    >
      <Space between align="end">
        <SpaceVertical gap="none" flexGrow={1}>
          <DsTypography variant="h3">Bulk Upload {title}</DsTypography>

          {isOverLimit && (
            <DsTypography color="error" variant="label">
              Limit of {MAX_BULK_ITEMS} entries reached ({itemCount}/{MAX_BULK_ITEMS})
            </DsTypography>
          )}
        </SpaceVertical>

        <Space justify="end">
          <DsRadioGroup
            name={radioGroupId}
            orientation="horizontal"
            value={expressionType === 'target' ? 'target' : 'block'}
            disabled={disableExpression}
            onValueChange={(e) => setExpressionType(e.detail as 'target' | 'block')}
          >
            <DsRadio name={radioGroupId} label="Include" value="target" />
            <DsRadio name={radioGroupId} label="Exclude" value="block" />
          </DsRadioGroup>
        </Space>
      </Space>

      {submissionError && <BINotification intent="error" text={submissionError} noActions={true} />}

      {invalidLines.length > 0 && (
        <BulkUploadFeedback title={`Bulk Upload: ${invalidLines.length} lines were not parsed.`} items={invalidLines} />
      )}

      <Box width={'100%'} flex={1} minHeight={'300px'} height="100%" position="relative">
        <StyledTextArea
          height="100%"
          value={rawInput}
          resize={false}
          onChange={(e) => setRawInput(e.target.value)}
          onBlur={() => setRawInput(validItems.join('\n'))}
          placeholder="Enter items, one per line..."
          style={{
            border: isOverLimit ? `1px solid ${globalTheme.colors.error}` : undefined,
          }}
        />
      </Box>

      <FileDropzone title={title} onFileSelect={handleFileDrop} />

      <Space justify="end" gap="u3">
        <BIButton color="secondary" onClick={onCancel}>
          Cancel
        </BIButton>
        <BIButton disabled={validItems.length === 0 || isOverLimit || isLoading} onClick={handleSubmit}>
          {isLoading || isProcessingFile ? <Spinner size={16} color="white" /> : 'Ok'}
        </BIButton>
      </Space>
    </SpaceVertical>
  );
};
